"use strict";
import { ONE, Protocol } from "@uniswap/router-sdk";
import { Fraction, Percent, TradeType } from "@uniswap/sdk-core";
import { slippageToleranceToPercent } from "features/transactions/swap/utils/format";
import { ACROSS_DAPP_INFO, isBridge, isClassic, isUniswapX } from "features/transactions/swap/utils/routing";
import { getClassicQuoteFromResponse } from "features/transactions/swap/utils/tradingApi";
import {
  TransactionType
} from "features/transactions/types/transactionDetails";
import { getSymbolDisplayText } from "utils/currency";
import { currencyId } from "utils/uniswapCurrencyId";
import { NumberType } from "utilities/src/format/types";
export function tradeToTransactionInfo(trade, transactedUSDValue, gasEstimates) {
  const slippageTolerancePercent = slippageToleranceToPercent(trade.slippageTolerance ?? 0);
  const { quote, slippageTolerance } = trade;
  const { quoteId, gasUseEstimate, routeString } = getClassicQuoteFromResponse(quote) ?? {};
  const inputCurrency = isUniswapX(trade) ? trade.inputAmount.currency.wrapped : trade.inputAmount.currency;
  const outputCurrency = trade.outputAmount.currency;
  if (isBridge(trade)) {
    return {
      type: TransactionType.Bridge,
      inputCurrencyId: currencyId(inputCurrency),
      inputCurrencyAmountRaw: trade.inputAmount.quotient.toString(),
      outputCurrencyId: currencyId(outputCurrency),
      outputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
      routingDappInfo: ACROSS_DAPP_INFO,
      quoteId,
      gasUseEstimate,
      transactedUSDValue,
      gasEstimates
    };
  }
  const baseTransactionInfo = {
    type: TransactionType.Swap,
    inputCurrencyId: currencyId(inputCurrency),
    outputCurrencyId: currencyId(outputCurrency),
    slippageTolerance,
    quoteId,
    gasUseEstimate,
    routeString,
    protocol: getProtocolVersionFromTrade(trade),
    simulationFailureReasons: isClassic(trade) ? trade.quote?.quote.txFailureReasons : void 0,
    transactedUSDValue,
    gasEstimates
  };
  return trade.tradeType === TradeType.EXACT_INPUT ? {
    ...baseTransactionInfo,
    tradeType: TradeType.EXACT_INPUT,
    inputCurrencyAmountRaw: trade.inputAmount.quotient.toString(),
    expectedOutputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
    minimumOutputCurrencyAmountRaw: trade.minimumAmountOut(slippageTolerancePercent).quotient.toString()
  } : {
    ...baseTransactionInfo,
    tradeType: TradeType.EXACT_OUTPUT,
    outputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
    expectedInputCurrencyAmountRaw: trade.inputAmount.quotient.toString(),
    maximumInputCurrencyAmountRaw: trade.maximumAmountIn(slippageTolerancePercent).quotient.toString()
  };
}
const MAX_ACCEPT_NEW_TRADE_THRESHOLD = new Percent(1, 100);
function getAcceptNewTradeThreshold(newTrade) {
  const slippage = newTrade.slippageTolerance;
  if (slippage === void 0) {
    return MAX_ACCEPT_NEW_TRADE_THRESHOLD;
  }
  const slippagePercent = slippageToleranceToPercent(slippage);
  return slippagePercent.lessThan(MAX_ACCEPT_NEW_TRADE_THRESHOLD) ? slippagePercent : MAX_ACCEPT_NEW_TRADE_THRESHOLD;
}
export function requireAcceptNewTrade(oldTrade, newTrade) {
  if (!oldTrade || !newTrade) {
    return false;
  }
  const acceptNewTradeThreshold = getAcceptNewTradeThreshold(newTrade);
  const isExecutionPriceWithinThreshold = isBridge(newTrade) ? !newTrade.executionPrice.lessThan(
    // Bridge trades have no slippage and hence a static execution price, so we calculate the threshold here.
    new Fraction(ONE).subtract(acceptNewTradeThreshold).multiply(oldTrade.executionPrice)
  ) : !newTrade.executionPrice.lessThan(oldTrade.worstExecutionPrice(acceptNewTradeThreshold));
  return oldTrade.tradeType !== newTrade.tradeType || !oldTrade.inputAmount.currency.equals(newTrade.inputAmount.currency) || !oldTrade.outputAmount.currency.equals(newTrade.outputAmount.currency) || !isExecutionPriceWithinThreshold;
}
export const calculateRateLine = (usdAmountOut, outputCurrencyAmount, trade, showInverseRate, formatter) => {
  const isValidAmounts = usdAmountOut && outputCurrencyAmount;
  const outputRateAmount = isValidAmounts ? parseFloat(usdAmountOut.toSignificant()) / parseFloat(outputCurrencyAmount.toSignificant()) : null;
  const inputRateAmount = outputRateAmount && trade ? outputRateAmount * parseFloat(trade.executionPrice.toSignificant()) : null;
  const rateToDisplay = showInverseRate ? outputRateAmount : inputRateAmount;
  const latestFiatPriceFormatted = formatter.convertFiatAmountFormatted(rateToDisplay, NumberType.FiatTokenPrice);
  return latestFiatPriceFormatted;
};
export const getRateToDisplay = (formatter, trade, showInverseRate) => {
  const price = showInverseRate ? trade.executionPrice.invert() : trade.executionPrice;
  let formattedPrice;
  try {
    formattedPrice = formatter.formatNumberOrString({
      value: price.toSignificant(),
      type: NumberType.SwapPrice
    });
  } catch (error) {
    formattedPrice = "0";
  }
  const quoteCurrencySymbol = getSymbolDisplayText(trade.executionPrice.quoteCurrency.symbol);
  const baseCurrencySymbol = getSymbolDisplayText(trade.executionPrice.baseCurrency.symbol);
  const rate = `1 ${quoteCurrencySymbol} = ${formattedPrice} ${baseCurrencySymbol}`;
  const inverseRate = `1 ${baseCurrencySymbol} = ${formattedPrice} ${quoteCurrencySymbol}`;
  return showInverseRate ? rate : inverseRate;
};
export function getProtocolVersionFromTrade(trade) {
  if (!isClassic(trade)) {
    return void 0;
  }
  if (trade.routes.every((r) => r.protocol === Protocol.V2)) {
    return Protocol.V2;
  }
  if (trade.routes.every((r) => r.protocol === Protocol.V3)) {
    return Protocol.V3;
  }
  return Protocol.MIXED;
}
export function validateTransactionRequest(request) {
  if (request?.to && request.chainId) {
    return { ...request, to: request.to, chainId: request.chainId };
  }
  return void 0;
}
export function validateTransactionRequests(requests) {
  if (!requests?.length) {
    return void 0;
  }
  const validatedRequests = [];
  for (const request of requests) {
    const validatedRequest = validateTransactionRequest(request);
    if (!validatedRequest) {
      return void 0;
    }
    validatedRequests.push(validatedRequest);
  }
  const [firstRequest, ...restRequests] = validatedRequests;
  return firstRequest ? [firstRequest, ...restRequests] : void 0;
}
export function validatePermit(permit) {
  const { domain, types, values } = permit ?? {};
  if (domain && types && values) {
    return { domain, types, values };
  }
  return void 0;
}
